import { ChevronDown, ChevronRight } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { StatusChip } from '@/components/editorial';
import { grantCurrency, grantRemaining, signatureRowMsg, type GrantRemaining } from '@/lib/money';
import { familyGrantStatusLabel, familyGrantStatusTone, grantTypeLabel } from '@/lib/grants-display';
import { grantExpenseLabel } from '@/lib/bills-display';
import { useMsg } from '@/i18n/msg';
import { cn } from '@/lib/utils';
import type { GrantRequest } from '@/types/api';

/**
 * One line per grant (2026-09-21, spec Part 3 item 3).
 *
 * Victor: "Let's create clarity around applying/renewing/managing and view
 * activity how much of a grant have I consumed." This row is the answer to
 * the last clause, and it is the page's primary reading — which is why the
 * money sits ON the row rather than inside a card the reader has to open.
 *
 * Slots, left to right (§3.1): identity, the status word, the money, the
 * disclosure chevron. The thin applied/pending bar sits under the money on
 * the right, so a column of rows can be compared down the page.
 *
 * NO G-NUMBER HERE, deliberately. An early draft printed `Signature · G-0587`
 * the way the staff grant lists do. It is withheld from the family wire on
 * purpose (`backend/src/helpers/familyFacingProjection.js`, 2026-09-19 R6/R8:
 * "display-only for authenticated staff, never on the family wire — a
 * sequence enumerates the program"), so the field is not even present on this
 * payload. A family's grant is identified by what it is FOR — the type and
 * the expense it covers — which is also what they would call it on a call.
 *
 * WHAT IS DELIBERATELY ABSENT
 *
 * No money line at all on an undecided grant. A request under review has no
 * budget, and `$0 left of $0` would be a false statement about a family's
 * money (§1.9). The row then carries its status word and nothing numeric —
 * which is the honest reading of "we have not decided yet".
 *
 * Amounts print in the award's own currency (`grantCurrency`), so a Canadian
 * award reads `CA$1,500` rather than being silently re-denominated.
 */
export interface GrantRowProps {
  grant: GrantRequest;
  expanded: boolean;
  onToggle: () => void;
  /**
   * Linked receipts waiting on the family (2026-10-03). Before this the only
   * sign of them was inside the expanded grant, so a to-do could sit unseen
   * under a closed row.
   */
  todoCount?: number;
  /**
   * #2745: this is the Signature grant an approved renewal created, and staff
   * have not Decided it yet (`renewalGrantInSetup` in lib/grants-display). The
   * row says the renewal was approved and the grant is being set up, because
   * until Decide it has no amount or schedule to say anything else with.
   */
  renewalInSetup?: boolean;
  /**
   * L5-a: the newest Signature grant, not yet Decided, with no approved
   * renewal to name (`signatureGrantInSetup` in lib/grants-display).
   * Admission creates it `submitted`, so it has no amount or schedule yet; the
   * row says TRF is setting it up and that both will appear here.
   * `renewalInSetup` wins when both are set: it says the same and names the
   * renewal.
   */
  awardInSetup?: boolean;
}

export function GrantRow({
  grant, expanded, onToggle, todoCount = 0, renewalInSetup = false, awardInSetup = false,
}: GrantRowProps) {
  const { t } = useTranslation('grants');
  const resolve = useMsg();
  const currency = grantCurrency(grant);
  const usage = grant.usage;
  // Only a DECIDED grant has a budget to be consumed, and `grantRemaining`
  // answers null when there is none — the row then prints its status word
  // and nothing numeric, rather than inventing `$0 left of $0`. The wording
  // lives in lib/money so the row and the bar cannot drift apart.
  const remaining = grantRemaining(usage, currency);
  // "$1,000 a month · 3 of 12 paid" under a Signature grant (2026-09-19);
  // "… · starts November 2026" while no payment is recorded (D-2394).
  const scheduleLine = resolve(signatureRowMsg(grant)) || null;
  // What the grant covers, translated (#2393). It used to be
  // `category_detail`, the staff record of the ask, printed verbatim.
  const expense = grantExpenseLabel(grant, t);
  // "Rent or mortgage · $1,000 a month · 3 of 12 paid": the `·` is the
  // catalog's (#2109 R3-B3), so a language can use its own separator.
  const subline = expense && scheduleLine
    ? t('row.detailAndSchedule', { detail: expense, schedule: scheduleLine })
    : expense || scheduleLine;

  const Chevron = expanded ? ChevronDown : ChevronRight;

  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={expanded}
      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
    >
      <span className="flex-1 min-w-0">
        <span className="block truncate text-body font-medium text-foreground">
          {grantTypeLabel(grant.type, t)}
        </span>
        {/* #2427: wraps rather than truncating. At 390 px this line holds
            about twice its box, and the ellipsis cut exactly the monthly
            amount and the month count a family opened the page to read. */}
        {subline && (
          <span
            className="block break-words text-body-sm text-stevens-ink-3"
            data-testid={scheduleLine ? 'grant-schedule-line' : undefined}
          >
            {subline}
          </span>
        )}
        {/* #2745: the approved renewal, named, while its grant has no amount yet. */}
        {renewalInSetup && (
          <span className="block break-words text-body-sm text-foreground" data-testid="grant-renewal-setup">
            {t('row.renewalSetup')}
          </span>
        )}
        {/* L5-a: an admitted family's Signature grant, before Decide. */}
        {awardInSetup && !renewalInSetup && (
          <span className="block break-words text-body-sm text-foreground" data-testid="grant-award-setup">
            {t('row.awardSetup')}
          </span>
        )}
        {/* The money cell on the right is hidden below sm (it needs 11rem a
            phone row does not have), so a phone reads the remaining amount
            here instead (#2427). Same sentence, same rule: no line at all on
            an undecided grant. */}
        {remaining && usage && (
          <span className="mt-1 block text-body-sm tabular-nums sm:hidden" data-testid="grant-money-compact">
            <RemainingText remaining={remaining} />
          </span>
        )}
      </span>

      {todoCount > 0 && (
        <StatusChip tone="warn" className="flex-shrink-0" data-testid="grant-todo-count">
          {t('row.todo', { count: todoCount })}
        </StatusChip>
      )}

      {/* #2821: the family's word; a Signature grant TRF created reads Awarded. */}
      <StatusChip tone={familyGrantStatusTone(grant)} className="flex-shrink-0">
        {familyGrantStatusLabel(grant, t)}
      </StatusChip>

      {remaining && usage && (
        <span className="flex-shrink-0 w-44 hidden sm:block" data-testid="grant-money">
          <span className="block text-right text-body-sm tabular-nums">
            <RemainingText remaining={remaining} />
          </span>
          <span className="mt-1 flex h-1.5 w-full overflow-hidden rounded-full bg-muted">
            <span
              className={cn('h-full', usage.over_applied ? 'bg-status-danger-fg' : 'bg-stevens-indigo')}
              style={{ width: `${pct(usage.applied_cents, usage.approved_cents)}%` }}
            />
            <span
              className="h-full bg-stevens-indigo/30"
              style={{ width: `${pct(usage.pending_cents, usage.approved_cents)}%` }}
            />
          </span>
        </span>
      )}

      <Chevron className="h-4 w-4 text-stevens-ink-3 flex-shrink-0" aria-hidden="true" />
    </button>
  );
}

/**
 * "$1,240 left of $3,000", or "Over budget" — printed by the money cell and,
 * below sm, by the row's own phone line (#2427).
 */
function RemainingText({ remaining }: { remaining: GrantRemaining }) {
  const { t } = useTranslation('grants');
  const resolve = useMsg();
  if (remaining.overBudget) {
    return <span className="font-medium text-status-danger-fg">{resolve(remaining.text)}</span>;
  }
  // The amount and the quiet rest are two styled halves of ONE catalog
  // sentence, so the word order is each language's own.
  return (
    <Trans
      t={t}
      i18nKey="remaining.leftOf"
      values={{ amount: remaining.amount, approved: remaining.approved }}
      components={{
        amount: <span className="font-medium text-foreground" />,
        rest: <span className="text-stevens-ink-3" />,
      }}
    />
  );
}

/** Share of the approved budget, clamped — a bar never runs past its track. */
function pct(part: number | null | undefined, whole: number | null | undefined): number {
  if (!whole || whole <= 0) return 0;
  return Math.min(100, Math.max(0, Math.round(((part ?? 0) / whole) * 100)));
}
